import { z } from 'zod';
import * as glossaire_type_de_couche from '../../ressources/glossaires/glossaire_type_de_couche';
import { creerEntierOuNull, texteFacultatif } from './champs';
import { coordonneesDesBornes, entierPositifOuNull } from './vue';

const TYPES_DE_COUCHE = Object.values(glossaire_type_de_couche);

/**
 * Schéma de la charge utile envoyée à l'ajout d'une couche de vue.
 *
 * AjouterCouche construit une charge utile différente selon le type de
 * couche : chaque type remplit son propre champ et vide les autres. Le schéma
 * déclare toutes les clés, z.object retirant celles qu'il ne connaît pas, puis
 * superRefine exige le champ propre au type choisi. Sans lui, une couche sans
 * image, sans URL ou sans contenu partait et s'affichait vide.
 *
 * bounds_image était converti par IsArrayString, qui remplaçait en silence une
 * saisie illisible par [[0,0],[0,0]] : l'image se retrouvait réduite à un
 * point sans que l'utilisateur le sache. La saisie est désormais refusée avec
 * le format attendu.
 */
export const schemaNouvelleCouche = z
  .object({
    layer_type: creerEntierOuNull({ message: 'valeur_invalide' }).refine(
      (valeur) => TYPES_DE_COUCHE.includes(valeur),
      { message: 'valeur_invalide' }
    ),
    layer_name: z
      .string({ message: 'saisir_un_nom_de_couche' })
      .trim()
      .min(1, { message: 'saisir_un_nom_de_couche' }),

    // Facultatifs : le formulaire les initialise à 0, et parseInt d'un champ
    // vidé donnait NaN, envoyé en null.
    projection_id: entierPositifOuNull,
    position: entierPositifOuNull,

    image_base64: texteFacultatif,
    // Deux points pour une image, chaîne vide pour les autres types.
    bounds_image: z.union([z.literal(''), coordonneesDesBornes], {
      message: 'format_coordonnees_attendu',
    }),
    geojson_content: texteFacultatif,
    tiles_url: texteFacultatif,

    // Présent pour le seul type QGIS WMS, absent sinon.
    qgis_wms: z
      .looseObject({
        url: texteFacultatif,
        parametre: z.looseObject({}).optional(),
      })
      .optional(),
  })
  .superRefine((couche, ctx) => {
    const estVide = (texte) => typeof texte !== 'string' || texte.trim() === '';

    switch (couche.layer_type) {
      case glossaire_type_de_couche.Image:
        if (estVide(couche.image_base64)) {
          ctx.addIssue({
            code: 'custom',
            path: ['image_base64'],
            message: 'importer_une_image',
          });
        }
        if (couche.bounds_image === '') {
          ctx.addIssue({
            code: 'custom',
            path: ['bounds_image'],
            message: 'format_coordonnees_attendu',
          });
        }
        break;
      case glossaire_type_de_couche.Tuile:
        if (estVide(couche.tiles_url)) {
          ctx.addIssue({
            code: 'custom',
            path: ['tiles_url'],
            message: 'saisir_une_url',
          });
        }
        break;
      case glossaire_type_de_couche.GeoJSON:
        if (!estJsonValide(couche.geojson_content)) {
          ctx.addIssue({
            code: 'custom',
            path: ['geojson_content'],
            message: 'saisir_un_geojson_valide',
          });
        }
        break;
      case glossaire_type_de_couche.QgisWms:
        if (!couche.qgis_wms || estVide(couche.qgis_wms.url)) {
          ctx.addIssue({
            code: 'custom',
            path: ['qgis_wms'],
            message: 'saisir_une_url',
          });
        }
        break;
      default:
        break;
    }
  });

/**
 * Le contenu GeoJSON part en texte, tel que saisi : on vérifie seulement qu'il
 * s'agit d'un objet JSON, sans imposer la structure GeoJSON complète.
 */
const estJsonValide = (texte) => {
  if (typeof texte !== 'string' || texte.trim() === '') {
    return false;
  }
  try {
    const valeur = JSON.parse(texte);
    return typeof valeur === 'object' && valeur !== null;
  } catch (e) {
    return false;
  }
};
